/**
 * Subscriber Analytics Panel
 *
 * Surfaces "who is subscribing, how many, how often, and trends" for admins
 * on the Users tab. Reads from /api/admin/analytics/subscribers.
 *
 * KPIs: active / trialing / past-due / canceled-in-range / new-in-range / MRR
 * Charts: daily new subscriptions, plan + status breakdown, renewal cadence
 * Table: most recent subscribers in the selected window
 */
import {
  Activity,
  AlertCircle,
  CreditCard,
  DollarSign,
  Loader2,
  RefreshCcw,
  TrendingUp,
  UserPlus,
  Users,
} from "lucide-react";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { Badge } from "@/components/ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  useAdminSubscribers,
  type AdminSubscribers,
  type AnalyticsRange,
} from "@/hooks/useAdminAnalytics";

interface Props {
  range: AnalyticsRange;
}

function formatDate(iso: string | null): string {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return iso;
  }
}

function formatMoney(amount: number | null | undefined): string {
  if (amount === null || amount === undefined || Number.isNaN(amount)) {
    return "—";
  }
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      maximumFractionDigits: 0,
    }).format(amount);
  } catch {
    return `$${amount.toFixed(0)}`;
  }
}

function statusBadgeClass(status: string | null): string {
  switch (status) {
    case "active":
      return "bg-green-100 text-green-800 border-green-200";
    case "trialing":
      return "bg-blue-100 text-blue-800 border-blue-200";
    case "past_due":
      return "bg-amber-100 text-amber-800 border-amber-200";
    case "canceled":
      return "bg-slate-100 text-slate-700 border-slate-200";
    case "paused":
      return "bg-purple-100 text-purple-800 border-purple-200";
    case "expired":
      return "bg-red-100 text-red-800 border-red-200";
    default:
      return "bg-slate-100 text-slate-700 border-slate-200";
  }
}

// Brand-aligned palette per plan. Falls back to slate if a plan name
// isn't recognized.
const PLAN_COLORS: Record<string, { monthly: string; yearly: string }> = {
  free: { monthly: "#94A3B8", yearly: "#64748B" },
  starter: { monthly: "#B87333", yearly: "#8B5A26" },
  expert: { monthly: "#3B82F6", yearly: "#1D4ED8" },
  elite: { monthly: "#8B5CF6", yearly: "#6D28D9" },
};

function planColors(name: string): { monthly: string; yearly: string } {
  return PLAN_COLORS[name] ?? { monthly: "#64748B", yearly: "#475569" };
}

function PlanBreakdownCharts({
  plans,
}: {
  plans: AdminSubscribers["by_plan"];
}) {
  // Hide the "free" tier from the revenue comparison (no revenue) but keep
  // it on the subscriber count chart for completeness.
  const subsData = plans.map((p) => ({
    plan: p.display_name,
    name: p.name,
    Monthly: p.monthly_count,
    Yearly: p.yearly_count,
    Total: p.active_count,
  }));

  // Revenue per plan, normalized to monthly recurring revenue (yearly/12)
  // so we're comparing apples-to-apples.
  const revenueData = plans
    .filter((p) => p.price_monthly > 0 || p.price_yearly > 0)
    .map((p) => {
      const monthly = p.monthly_count * p.price_monthly;
      const yearly = p.yearly_count * (p.price_yearly / 12);
      return {
        plan: p.display_name,
        name: p.name,
        "Monthly MRR": Math.round(monthly),
        "Yearly MRR": Math.round(yearly),
        Total: Math.round(monthly + yearly),
      };
    });

  const totalActive = subsData.reduce((sum, p) => sum + p.Total, 0);
  const totalMrr = revenueData.reduce((sum, p) => sum + p.Total, 0);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Breakdown by Plan</CardTitle>
        <CardDescription>
          Compare active + trialing subscribers and estimated MRR across plans,
          split by monthly vs yearly billing.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {plans.length === 0 ? (
          <p className="text-sm text-slate-500">No subscription plans found.</p>
        ) : (
          <div className="space-y-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div>
                <div className="flex items-baseline justify-between mb-2">
                  <h4 className="text-sm font-semibold text-slate-700">
                    Subscribers per Plan
                  </h4>
                  <span className="text-xs text-slate-500">
                    {totalActive.toLocaleString()} total active + trialing
                  </span>
                </div>
                <div className="h-72">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={subsData}
                      margin={{ top: 8, right: 8, left: 0, bottom: 8 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="plan" tick={{ fontSize: 12 }} />
                      <YAxis allowDecimals={false} />
                      <Tooltip
                        formatter={(value, name) => [
                          Number(value).toLocaleString(),
                          String(name),
                        ]}
                      />
                      <Legend />
                      <Bar dataKey="Monthly" stackId="subs">
                        {subsData.map((d) => (
                          <Cell
                            key={`m-${d.name}`}
                            fill={planColors(d.name).monthly}
                          />
                        ))}
                      </Bar>
                      <Bar dataKey="Yearly" stackId="subs">
                        {subsData.map((d) => (
                          <Cell
                            key={`y-${d.name}`}
                            fill={planColors(d.name).yearly}
                          />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>

              <div>
                <div className="flex items-baseline justify-between mb-2">
                  <h4 className="text-sm font-semibold text-slate-700">
                    MRR per Plan
                  </h4>
                  <span className="text-xs text-slate-500">
                    {formatMoney(totalMrr)} / mo estimated
                  </span>
                </div>
                <div className="h-72">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={revenueData}
                      margin={{ top: 8, right: 8, left: 0, bottom: 8 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="plan" tick={{ fontSize: 12 }} />
                      <YAxis
                        tickFormatter={(v: number) =>
                          v >= 1000 ? `$${(v / 1000).toFixed(1)}k` : `$${v}`
                        }
                      />
                      <Tooltip
                        formatter={(value, name) => [
                          formatMoney(Number(value)),
                          String(name),
                        ]}
                      />
                      <Legend />
                      <Bar dataKey="Monthly MRR" stackId="rev">
                        {revenueData.map((d) => (
                          <Cell
                            key={`rm-${d.name}`}
                            fill={planColors(d.name).monthly}
                          />
                        ))}
                      </Bar>
                      <Bar dataKey="Yearly MRR" stackId="rev">
                        {revenueData.map((d) => (
                          <Cell
                            key={`ry-${d.name}`}
                            fill={planColors(d.name).yearly}
                          />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-slate-500 border-b">
                    <th className="py-2 pr-4 font-medium">Plan</th>
                    <th className="py-2 pr-4 font-medium text-right">Active</th>
                    <th className="py-2 pr-4 font-medium text-right">
                      Monthly
                    </th>
                    <th className="py-2 pr-4 font-medium text-right">Yearly</th>
                    <th className="py-2 pr-4 font-medium text-right">
                      Monthly Price
                    </th>
                    <th className="py-2 pr-4 font-medium text-right">
                      Yearly Price
                    </th>
                    <th className="py-2 font-medium text-right">Est. MRR</th>
                  </tr>
                </thead>
                <tbody>
                  {plans.map((p) => {
                    const mrr =
                      p.monthly_count * p.price_monthly +
                      p.yearly_count * (p.price_yearly / 12);
                    return (
                      <tr
                        key={p.subscription_type_id}
                        className="border-b last:border-b-0 hover:bg-slate-50"
                      >
                        <td className="py-2 pr-4 font-semibold text-slate-800 flex items-center gap-2">
                          <span
                            className="inline-block w-3 h-3 rounded-sm"
                            style={{
                              backgroundColor: planColors(p.name).monthly,
                            }}
                          />
                          {p.display_name}
                        </td>
                        <td className="py-2 pr-4 text-right">
                          {p.active_count.toLocaleString()}
                        </td>
                        <td className="py-2 pr-4 text-right text-slate-600">
                          {p.monthly_count.toLocaleString()}
                        </td>
                        <td className="py-2 pr-4 text-right text-slate-600">
                          {p.yearly_count.toLocaleString()}
                        </td>
                        <td className="py-2 pr-4 text-right text-slate-600">
                          {formatMoney(p.price_monthly)}
                        </td>
                        <td className="py-2 pr-4 text-right text-slate-600">
                          {formatMoney(p.price_yearly)}
                        </td>
                        <td className="py-2 text-right font-semibold text-slate-800">
                          {formatMoney(Math.round(mrr))}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export function SubscriberAnalyticsPanel({ range }: Props) {
  const { data, isLoading, error } = useAdminSubscribers(range);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-8 w-8 animate-spin text-[#B87333]" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <Card>
        <CardContent className="py-12 text-center text-slate-600">
          Unable to load subscriber analytics.
        </CardContent>
      </Card>
    );
  }

  const mrrDollars = data.mrr_cents / 100;
  const totalCadence =
    data.cadence.monthly + data.cadence.yearly + data.cadence.other;
  const monthlyPct =
    totalCadence > 0
      ? Math.round((data.cadence.monthly / totalCadence) * 100)
      : 0;
  const yearlyPct =
    totalCadence > 0
      ? Math.round((data.cadence.yearly / totalCadence) * 100)
      : 0;

  const stats = [
    {
      label: "Active Subscribers",
      value: data.active_subscribers,
      icon: <Users className="h-5 w-5" />,
    },
    {
      label: "Trialing",
      value: data.trialing_subscribers,
      icon: <Activity className="h-5 w-5" />,
    },
    {
      label: "Past Due",
      value: data.past_due_subscribers,
      icon: <AlertCircle className="h-5 w-5" />,
    },
    {
      label: "New (window)",
      value: data.new_subscribers_in_range,
      icon: <UserPlus className="h-5 w-5" />,
    },
    {
      label: "Canceled (window)",
      value: data.canceled_in_range,
      icon: <RefreshCcw className="h-5 w-5" />,
    },
    {
      label: "Est. MRR",
      value: formatMoney(mrrDollars),
      icon: <DollarSign className="h-5 w-5" />,
      tooltip:
        "Estimated monthly recurring revenue across active + trialing subscribers; yearly plans normalized to monthly.",
    },
  ];

  const dailyData = data.daily_new_subscriptions.map((d) => ({
    date: d.date.slice(5),
    count: d.count,
  }));

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-2xl font-bold text-slate-900 mb-1">
          Subscriber Analytics
        </h3>
        <p className="text-sm text-slate-600">
          Who is subscribing, how many, how often, and trends over the selected
          window.
        </p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        {stats.map((s) => (
          <Card key={s.label} className="border-2" title={s.tooltip}>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-xs font-medium text-slate-600">
                {s.label}
              </CardTitle>
              <div className="p-2 bg-slate-100 rounded-lg text-slate-600">
                {s.icon}
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-slate-900">
                {typeof s.value === "number"
                  ? s.value.toLocaleString()
                  : s.value}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-[#B87333]" /> New
              Subscriptions Over Time
            </CardTitle>
            <CardDescription>
              Daily new subscription records created in this window.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={dailyData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis allowDecimals={false} />
                  <Tooltip />
                  <Area
                    type="monotone"
                    dataKey="count"
                    stroke="#B87333"
                    fill="#B87333"
                    fillOpacity={0.2}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CreditCard className="h-5 w-5 text-[#B87333]" /> Renewal Cadence
            </CardTitle>
            <CardDescription>
              How often subscribers renew (monthly vs yearly) across active +
              trialing.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {totalCadence === 0 ? (
              <p className="text-sm text-slate-500">
                No active or trialing subscribers in this snapshot.
              </p>
            ) : (
              <div className="space-y-4">
                <div>
                  <div className="flex items-center justify-between text-sm font-medium text-slate-700">
                    <span>Monthly</span>
                    <span>
                      {data.cadence.monthly.toLocaleString()} ({monthlyPct}%)
                    </span>
                  </div>
                  <div className="w-full h-3 bg-slate-100 rounded-full overflow-hidden mt-1">
                    <div
                      className="h-full bg-[#B87333]"
                      style={{ width: `${monthlyPct}%` }}
                    />
                  </div>
                </div>
                <div>
                  <div className="flex items-center justify-between text-sm font-medium text-slate-700">
                    <span>Yearly</span>
                    <span>
                      {data.cadence.yearly.toLocaleString()} ({yearlyPct}%)
                    </span>
                  </div>
                  <div className="w-full h-3 bg-slate-100 rounded-full overflow-hidden mt-1">
                    <div
                      className="h-full bg-amber-500"
                      style={{ width: `${yearlyPct}%` }}
                    />
                  </div>
                </div>
                {data.cadence.other > 0 && (
                  <div className="text-xs text-slate-500">
                    Other cycles: {data.cadence.other.toLocaleString()}
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <PlanBreakdownCharts plans={data.by_plan} />

      <Card>
        <CardHeader>
          <CardTitle>Breakdown by Status</CardTitle>
          <CardDescription>
            All subscription rows grouped by current status.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {data.by_status.length === 0 ? (
            <p className="text-sm text-slate-500">No subscriptions found.</p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {data.by_status.map((s) => (
                <Badge
                  key={s.status}
                  variant="outline"
                  className={`px-3 py-1 text-sm ${statusBadgeClass(s.status)}`}
                >
                  {s.status}: {s.count.toLocaleString()}
                </Badge>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Recent Subscribers</CardTitle>
          <CardDescription>
            Up to 50 most recent subscription records created in this window.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {data.recent_subscribers.length === 0 ? (
            <p className="text-sm text-slate-500">
              No new subscribers in this range.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-slate-500 border-b">
                    <th className="py-2 pr-4 font-medium">User</th>
                    <th className="py-2 pr-4 font-medium">Plan</th>
                    <th className="py-2 pr-4 font-medium">Status</th>
                    <th className="py-2 pr-4 font-medium">Cycle</th>
                    <th className="py-2 pr-4 font-medium">Subscribed</th>
                    <th className="py-2 pr-4 font-medium">Renews</th>
                    <th className="py-2 font-medium text-right">
                      Last Payment
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {data.recent_subscribers.map((s) => (
                    <tr
                      key={s.subscription_id}
                      className="border-b last:border-b-0 hover:bg-slate-50 align-top"
                    >
                      <td className="py-2 pr-4">
                        <div className="font-medium text-slate-800">
                          {s.full_name || "—"}
                        </div>
                        <div className="text-xs text-slate-500">
                          {s.email || "—"}
                        </div>
                      </td>
                      <td className="py-2 pr-4 text-slate-700">
                        {s.plan_name || "—"}
                      </td>
                      <td className="py-2 pr-4">
                        <Badge
                          variant="outline"
                          className={`px-2 py-0.5 ${statusBadgeClass(s.status)}`}
                        >
                          {s.status || "unknown"}
                        </Badge>
                      </td>
                      <td className="py-2 pr-4 text-slate-700 capitalize">
                        {s.billing_cycle || "—"}
                      </td>
                      <td className="py-2 pr-4 text-slate-700">
                        {formatDate(s.created_at)}
                      </td>
                      <td className="py-2 pr-4 text-slate-700">
                        {s.status === "canceled"
                          ? `canceled ${formatDate(s.canceled_at)}`
                          : formatDate(s.current_period_end)}
                      </td>
                      <td className="py-2 text-right text-slate-700">
                        {s.last_payment_amount !== null
                          ? formatMoney(s.last_payment_amount)
                          : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default SubscriberAnalyticsPanel;
